import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * Restores the page position immediately, independently of any global
 * `scroll-behavior: smooth` rule used by the theme.
 * @param {number} scrollY
 */
function restoreScrollImmediately(scrollY) {
  const root = document.documentElement;
  const previousScrollBehavior = root.style.scrollBehavior;

  root.style.scrollBehavior = 'auto';
  window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });

  requestAnimationFrame(() => {
    root.style.scrollBehavior = previousScrollBehavior;
  });
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number} */
  #drawerScrollY = 0;

  /** @type {ScrollRestoration} */
  #previousScrollRestoration = 'auto';

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.addEventListener('pageshow', this.#handlePageShow);
    window.addEventListener('pagehide', this.#handlePageHide);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#releasePageScroll);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('pagehide', this.#handlePageHide);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#releasePageScroll);
    this.#historyAbortController?.abort();
    history.scrollRestoration = this.#previousScrollRestoration;
    this.#releasePageScroll();
  }

  /**
   * Clears a stale page scroll lock left by an interrupted dialog close.
   * This protects Mobile Safari back/forward-cache and swipe navigation flows.
   */
  #releasePageScroll = () => {
    const otherLockingDialog = document.querySelector('dialog[open][scroll-lock]');
    if (otherLockingDialog && otherLockingDialog !== this.refs.dialog) return;

    const lockedScrollY = Math.abs(Number.parseInt(document.body.style.top || '0', 10)) || window.scrollY;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.documentElement.removeAttribute('scroll-lock');

    if (lockedScrollY > 0 && window.scrollY !== lockedScrollY) {
      restoreScrollImmediately(lockedScrollY);
    }
  };

  /**
   * Prevents an open cart dialog and fixed-body state from being stored in the
   * browser back-forward cache.
   */
  #handlePageHide = () => {
    this.#historyAbortController?.abort();
    history.scrollRestoration = this.#previousScrollRestoration;

    if (this.refs.dialog?.open) {
      this.refs.dialog.close();
      this.refs.dialog.classList.remove('dialog-closing');
    }

    this.#releasePageScroll();
  };

  /**
   * Synchronizes cart count and drawer content when a page is restored via
   * browser back/swipe navigation.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = async (event) => {
    const navigation = performance.getEntriesByType('navigation')[0];
    const isHistoryRestore = event.persisted || navigation?.type === 'back_forward';

    if (!isHistoryRestore) return;

    if (this.refs.dialog?.open) {
      this.refs.dialog.close();
      this.refs.dialog.classList.remove('dialog-closing');
    }

    this.#releasePageScroll();

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) return;

      const cart = await response.json();
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-drawer-history-restore', {
          source: 'cart-drawer-history-restore',
          itemCount: cart.item_count,
        })
      );
    } catch (error) {
      console.warn('Cart could not be refreshed after history restore.', error);
    }
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      // This method is intentionally called before showDialog(). Creating the
      // history entry while the page still has its natural scroll position
      // prevents browsers from recording the previous entry at scrollY 0.
      const scrollY = window.scrollY;
      this.#drawerScrollY = scrollY;
      this.#previousScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';

      history.pushState(
        {
          cartDrawerOpen: true,
          cartDrawerScrollY: scrollY,
        },
        ''
      );
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    if (history.state?.cartDrawerOpen) {
      history.back();
      return;
    }

    // When closeDialog() is called from the popstate handler, the synthetic
    // drawer history entry has already been consumed. Only then is it safe to
    // detach the listener. Detaching it before history.back() lets the browser
    // restore an arbitrary scroll position on mobile Safari/Chrome.
    this.#historyAbortController?.abort();
  };

  #handlePopState = async () => {
    const dialog = this.refs.dialog;

    try {
      if (dialog?.open) {
        dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
        await this.closeDialog();
      }
    } finally {
      dialog?.style.removeProperty('--dialog-drawer-closing-animation');

      // Browser swipe-back restoration and dialog scroll restoration can run
      // in different orders on iOS and Android. Apply one final deterministic
      // restoration only after both flows have completed.
      this.#releasePageScroll();
      const scrollY = this.#drawerScrollY;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          restoreScrollImmediately(scrollY);
          history.scrollRestoration = this.#previousScrollRestoration;
        });
      });
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.#handleHistoryOpen();
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.#handleHistoryOpen();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
